// Nhóm PHẦN NỘI ĐỊA của số điện thoại theo cách người nước đó đọc số của mình — chỉ để
// HIỂN THỊ trong ô nhập (`common/PhoneInput`). Không phải luật số.
//
// Luật (mã quốc gia, độ dài, quy ước lưu) vẫn CHỈ ở `phonePolicy.js` — tệp sinh ra từ kernel
// BE, đừng chép sang đây. Tệp này không quyết định số nào hợp lệ: nó chỉ chèn dấu cách / ngoặc /
// gạch vào chữ số người ta đang gõ. Ô nhập vẫn phát ra chuỗi chữ số trần qua `composePhone`,
// nên máy chủ không bao giờ thấy các dấu này.
//
// Vì sao có (sếp Phú 23/09/2026, ảnh trang đăng ký): chọn 🇨🇦 +1 rồi gõ `2265035441` thì ô
// hiện mười chữ số liền nhau — mắt không soát lại được, thừa hay thiếu một số vẫn "trông giống
// số của mình". Trang đăng nhập bằng mã SMS đã nhóm `(226) 503-5441` từ 01/09; ô dùng chung
// thì chưa. Mẫu Bắc Mỹ ở đây CỐ Ý giống hệt `nhomSoBacMy` của trang đó (và của app).
//
// Chỉ khai những nước có khách/nhân sự thật hoặc nguồn thợ phổ biến ở Canada. Nước chưa khai
// thì để chữ số trần — thà không nhóm còn hơn nhóm sai kiểu người ta quen đọc.

import { parsePhone, findPhoneCountry } from './phonePolicy.js';

/**
 * Nhóm của từng nước: danh sách độ dài nhóm, đọc từ trái sang. Nước có hai kiểu số hay gặp
 * (di động / cố định) thì chọn theo chữ số đầu. Nguồn: cách libphonenumber in số quốc tế
 * (`+84 909 123 456`, `+44 7911 123456`…).
 */
const GROUPS = {
  VN: () => [3, 3, 4],                                   // 909 123 456 · 243 825 1234
  GB: (n) => (n.startsWith('7') ? [4, 6] : [2, 4, 4]),   // 7911 123456 · 20 7946 0958
  FR: () => [1, 2, 2, 2, 2],                             // 6 12 34 56 78
  AU: (n) => (n.startsWith('4') ? [3, 3, 3] : [1, 4, 4]), // 412 345 678 · 2 9876 5432
  SG: () => [4, 4],                                      // 8123 4567
  IN: () => [5, 5],                                      // 98765 43210
  PH: () => [3, 3, 4],                                   // 917 123 4567
  MX: () => [2, 4, 4],                                   // 55 1234 5678
  CN: () => [3, 4, 4],                                   // 131 2345 6789
  KR: () => [2, 4, 4],                                   // 10 1234 5678
  JP: () => [2, 4, 4],                                   // 90 1234 5678
};

/** Số mẫu (phần nội địa) để làm chữ gợi ý trong ô trống — in qua đúng hàm nhóm bên dưới. */
const EXAMPLE = {
  NANP: '6045551234',
  VN: '909123456',
  GB: '7911123456',
  FR: '612345678',
  AU: '412345678',
  SG: '81234567',
  IN: '9876543210',
  PH: '9171234567',
  MX: '5512345678',
  CN: '13123456789',
  KR: '1012345678',
  JP: '9012345678',
};

// Nước mà số 0 đứng đầu là MỘT PHẦN của số chứ không phải tiền tố gọi trong nước: Ý (06…),
// San Marino, Vatican, Bờ Biển Ngà (đổi sang 10 số mở đầu 0 từ 2021). Bỏ số 0 ở đây là ra số khác.
const ZERO_IS_PART_OF_NUMBER = new Set(['IT', 'SM', 'VA', 'CI']);

const isNanp = (country) => country?.dial === '1';

/// `226` → `226` · `226503` → `(226) 503` · `2265035441` → `(226) 503-5441`.
/// Chỉ đóng ngoặc TỪ CHỮ SỐ THỨ TƯ: đóng ngay khi vừa đủ ba số thì lúc xoá lùi người dùng phải
/// xoá qua một dấu ngoặc không do mình gõ.
function groupNanp(d) {
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Chèn dấu vào chuỗi chữ số nội địa `national` của nước `country` (một dòng PHONE_COUNTRIES).
 * Nhóm dần theo từng chữ số gõ vào. Dài hơn khuôn của nước (số sai) ⇒ trả chữ số trần, để
 * người gõ thấy ngay có gì đó lệch thay vì một con số nhóm đẹp mà sai.
 */
function groupNationalDigits(country, national) {
  const d = String(national || '').replace(/\D/g, '');
  if (!d || !country) return d;
  if (isNanp(country)) return d.length > 10 ? d : groupNanp(d);

  const pick = GROUPS[country.iso2];
  if (!pick) return d;
  const sizes = pick(d);
  if (d.length > sizes.reduce((a, b) => a + b, 0)) return d;
  const parts = [];
  let i = 0;
  for (const size of sizes) {
    if (i >= d.length) break;
    parts.push(d.slice(i, i + size));
    i += size;
  }
  return parts.join(' ');
}

/** Chữ gợi ý cho ô trống: số mẫu của nước đó, nhóm đúng kiểu. Nước chưa khai ⇒ null. */
function examplePhoneNational(country) {
  if (!country) return null;
  const sample = isNanp(country) ? EXAMPLE.NANP : EXAMPLE[country.iso2];
  return sample ? groupNationalDigits(country, sample) : null;
}

/**
 * Bỏ tiền tố gọi TRONG NƯỚC mà người ta gõ kèm theo thói quen, vì bộ chọn quốc gia bên trái đã
 * nói mã nước rồi:
 *   · Bắc Mỹ: `1` đứng đầu (mã vùng không bao giờ mở đầu bằng 1 — đó là mã nước gõ lại lần nữa)
 *   · phần lớn nước còn lại: `0` đứng đầu (VN `0909…`, Anh `07911…`, Pháp `06…`).
 *
 * Không bỏ thì: chọn 🇻🇳 +84 rồi gõ `0909123456` ⇒ ô ghép thành `00909123456` — máy chủ đọc
 * `00` là tiền tố quốc tế và hiểu thành số Thổ Nhĩ Kỳ (+90). Ô đó vẫn không viền đỏ vì mười
 * chữ số vẫn lọt dải độ dài của VN, còn nút gửi thì tắt mà không ai hiểu vì sao.
 * Cùng luật với `parsePhone` của kernel (số dạng nội địa ⇒ bỏ các số 0 đứng đầu).
 */
function stripTrunkPrefix(country, digits) {
  const d = String(digits || '');
  if (!country) return d;
  if (isNanp(country)) return d.startsWith('1') ? d.slice(1) : d;
  if (ZERO_IS_PART_OF_NUMBER.has(country.iso2)) return d;
  return d.replace(/^0+/, '');
}

/** Số chữ số nội địa tối đa của nước — chặn ngay lúc gõ thay vì để viền đỏ rồi đoán. */
function maxNationalDigits(country) {
  if (!country) return 15;
  if (Array.isArray(country.len)) return country.len[1];
  return 15 - String(country.dial || '').length;
}

/**
 * In cả số cho người đọc, kèm mã nước: `+12265035441` → `+1 (226) 503-5441`,
 * `0909123456` → `+84 909 123 456`. Không tách được thì trả nguyên chuỗi.
 */
function formatPhoneGrouped(value, defaultIso2) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return '';
  const p = parsePhone(raw, defaultIso2);
  if (!p.valid) return raw;
  return `+${p.dial} ${groupNationalDigits(findPhoneCountry(p.iso2), p.national)}`;
}

/** Vị trí trong chuỗi ĐÃ CHÈN DẤU ngay sau chữ số thứ `n` — để đặt lại con trỏ khi gõ giữa số. */
function caretAfterDigits(formatted, n) {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return formatted.length;
}

export {
  groupNationalDigits,
  formatPhoneGrouped,
  examplePhoneNational,
  stripTrunkPrefix,
  maxNationalDigits,
  caretAfterDigits,
};
